import { SkeletonBlock } from '../../components/SkeletonBlock';
import { ActionBar } from '../ActionBar';
import { ContentPanelShell, HeadingAccent } from '../ContentPanelShell';
import { ExtrasListCard } from '../ExtrasListCard';
import { ExtrasModals, useExtrasSelection } from '../ExtrasModals';
import { PanelActionFooter } from '../PanelActionFooter';
import { List } from './ExtrasContentPanel.styles';
import type { ExtrasContentPanelProps } from './ExtrasContentPanel.types';
import { radius } from '../../theme/tokens';

const SKELETON_ROWS = 4;

export function ExtrasContentPanel({
  items,
  totalAmount,
  currencyCode,
  locale,
  guests,
  currentStep,
  totalSteps,
  onBack,
  onAdd,
  onRemove,
  onConfirmGuestSelection,
  onConfirmSelection,
  onCtaClick,
  ctaDisabled,
  isLoading = false,
  labels,
}: ExtrasContentPanelProps) {
  const {
    title,
    subtitle,
    backAriaLabel,
    add,
    remove,
    quantity,
    editSelections,
    footerTitle,
    footerTotal,
    ctaLabel,
    progressLabel,
  } = labels;

  const selection = useExtrasSelection({
    items,
    guests,
    onAdd,
    onConfirmGuestSelection,
    onConfirmSelection,
  });

  return (
    <>
      <ContentPanelShell
        isLoading={isLoading}
        topBar={
          <ActionBar
            onBack={onBack}
            backAriaLabel={backAriaLabel}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
          />
        }
        heading={
          subtitle ? (
            <>
              {title} <HeadingAccent>{subtitle}</HeadingAccent>
            </>
          ) : (
            title
          )
        }
        footer={
          <PanelActionFooter
            title={footerTitle}
            totalLabel={footerTotal}
            totalAmount={totalAmount}
            currencyCode={currencyCode}
            locale={locale}
            ctaLabel={ctaLabel}
            onCtaClick={onCtaClick}
            ctaDisabled={ctaDisabled}
            isLoading={isLoading}
          />
        }
      >
        <List>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <SkeletonBlock
                  key={index}
                  height="122px"
                  borderRadius={radius('card', 'radius-lg')}
                />
              ))
            : items.map((item) => (
                <ExtrasListCard
                  key={item.id}
                  id={item.id}
                  image={item.image}
                  title={item.title}
                  price={{ ...item.price, locale }}
                  selected={item.selected}
                  quantity={item.quantity}
                  onAdd={() => selection.handleAdd(item)}
                  onRemove={() => onRemove(item.id)}
                  onEditSelections={
                    selection.canEditSelections(item) && item.selected
                      ? () => selection.handleEditSelections(item)
                      : undefined
                  }
                  labels={{ add, remove, quantity, editSelections }}
                />
              ))}
        </List>
      </ContentPanelShell>
      <ExtrasModals
        selection={selection}
        items={items}
        guests={guests}
        currencyCode={currencyCode}
        locale={locale}
        labels={labels}
      />
    </>
  );
}
